import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Calculator, Cpu, Lock } from 'lucide-react';
import ProjectDetails from '../PCC/ProjectDetails.js';
import ProjectComputation from '../PCC/ProjectComputation.js';
import Card3D from './Card3D.js';
import useIntro from './useIntro.js';
import useTheme from './useTheme.js';

function PCCIntroCurtain({ show, isLight }) {
  const accent = isLight ? '#2563eb' : '#facc15';
  const accentSoft = isLight ? 'rgba(37,99,235,0.78)' : 'rgba(250,204,21,0.82)';
  const pageBg = isLight ? '#ffffff' : '#0f172a';
  const pageBgGradient = isLight
    ? 'radial-gradient(1200px 800px at 12% -10%, rgba(219,234,254,0.95) 0%, rgba(255,255,255,0) 55%), radial-gradient(1100px 700px at 100% 120%, rgba(254,249,195,0.9) 0%, rgba(255,255,255,0) 55%), linear-gradient(135deg,#eff6ff 0%, #ffffff 45%, #fefce8 100%)'
    : undefined;
  const titleClass = isLight
    ? 'mt-6 font-display text-3xl font-semibold tracking-tight text-slate-900'
    : 'mt-6 font-display text-3xl font-semibold tracking-tight text-white';
  const captionClass = isLight
    ? 'mt-3 font-display text-xs text-slate-500 italic'
    : 'mt-3 font-display text-xs italic';
  const dividerBg = isLight ? 'rgba(15,23,42,0.12)' : 'rgba(255,255,255,0.08)';
  const tileCls = isLight
    ? 'relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white'
    : 'relative grid size-20 place-items-center rounded-[28px] bg-white p-2.5 border border-white/10';
  const tileStyle = isLight
    ? {
        borderColor: 'rgba(15,23,42,0.08)',
        boxShadow: '0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(15,23,42,0.22)',
      }
    : {
        boxShadow:
          '0 24px 60px -18px rgba(250,204,21,0.35), 0 0 0 1px rgba(250,204,21,0.15) inset',
      };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="pcc-intro-curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: 'blur(10px)' }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className="fixed inset-0 z-[140] grid place-items-center"
          style={{
            background: pageBgGradient || pageBg,
            backgroundColor: pageBg,
          }}
        >
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
              />
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={titleClass}
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              iSource PCC
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 text-[10px] font-bold tracking-[0.34em] uppercase"
              style={{ color: accentSoft }}
            >
              Approvals &amp; Computation Hub
            </motion.p>

            <div
              className="relative mt-8 h-px w-56 overflow-hidden"
              style={{ background: dividerBg }}
            >
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: accent }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={captionClass}
              style={{ color: isLight ? 'rgba(15,23,42,0.55)' : accentSoft }}
            >
              Loading approvals and project data…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function PCCPage({ axiosInstance, user = {} }) {
  const { isLight } = useTheme();
  const [minElapsed, setMinElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1400);
    return () => clearTimeout(t);
  }, []);
  const [activeTab, setActiveTabRaw] = useState('details');
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [exportReadyCount, setExportReadyCount] = useState(0);
  const isIntro = useIntro();

  const stagger = (i) =>
    isIntro
      ? { className: 'stagger-in', style: { '--i': i } }
      : { className: '', style: undefined };

  const isDgmUser =
    (user?.email?.trim().toLowerCase() === 'dgm@isourceasia.com') ||
    (user?.full_name?.toUpperCase() === 'DONALD G. MADINO');
  const isCadfUser = user?.email?.trim().toLowerCase() === 'cadf@isourceasiaworkplace.com';
  const isOamUser = user?.email?.trim().toLowerCase() === 'oam@isourceasiaworkplace.com';
  const isJcpUser = user?.email?.trim().toLowerCase() === 'jcp@isourceasia.com';
  const isMspUser = user?.email?.trim().toLowerCase() === 'msp@isourceasia.com.ph';

  const hasPccAccess = user?.permissions?.ccp_access || user?.email === 'vinsimon23@gmail.com';
  const canAccessComputation =
    (user?.is_super_admin && !isDgmUser) ||
    isCadfUser ||
    isOamUser ||
    isJcpUser ||
    isMspUser ||
    user?.email === 'vinsimon23@gmail.com';

  useEffect(() => {
    if (!hasPccAccess) return;

    let mounted = true;
    const fetchCounts = async () => {
      try {
        const res = await axiosInstance.get('/ccp/project-details');
        const details = Array.isArray(res.data) ? res.data : [];

        let pending = 0;
        let exportReady = 0;

        details.forEach((p) => {
          const isPlaceholder = p.project_type === 'Account_Placeholder';
          if (isPlaceholder) return;

          const isApproved = p.is_approved === true || p.is_approved === 'true' || p.is_approved === 1;
          const isApprovedPricing =
            p.is_approved_pricing === true || p.is_approved_pricing === 'true' || p.is_approved_pricing === 1;
          const isApprovedCosts =
            p.is_approved_costs === true || p.is_approved_costs === 'true' || p.is_approved_costs === 1;
          const isApprovedJoygie =
            p.is_approved_joygie === true || p.is_approved_joygie === 'true' || p.is_approved_joygie === 1;
          const isApprovedOverall =
            p.is_approved_overall === true || p.is_approved_overall === 'true' || p.is_approved_overall === 1;

          if (isDgmUser && !isApproved) pending++;
          else if (isCadfUser && isApproved && !isApprovedPricing) pending++;
          else if (isOamUser && isApprovedPricing && !isApprovedCosts) pending++;
          else if (isJcpUser && isApprovedCosts && !isApprovedJoygie) pending++;
          else if (isMspUser && isApprovedJoygie && !isApprovedOverall) pending++;

          if (isOamUser && isApprovedOverall) {
            const isExported =
              p.is_exported_by_oam === true ||
              p.is_exported_by_oam === 'true' ||
              p.is_exported_by_oam === 1;
            if (!isExported) exportReady++;
          }
        });

        if (mounted) {
          setPendingApprovalsCount(pending);
          setExportReadyCount(exportReady);
        }
      } catch (err) {
        console.warn('[PCCPage] Failed to fetch approval counts:', err?.message || err);
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [
    axiosInstance,
    hasPccAccess,
    isDgmUser,
    isCadfUser,
    isOamUser,
    isJcpUser,
    isMspUser,
  ]);

  if (!hasPccAccess) {
    return (
      <>
        <PCCIntroCurtain show={!minElapsed} isLight={isLight} />
        <div className="space-y-6">
        <div {...stagger(0)}>
          <Card3D className="content-glass rounded-xl p-12 text-center" intensity={6} lift={12}>
            <div
              className="w-16 h-16 rounded-2xl border flex items-center justify-center mx-auto mb-4"
              style={{
                background:
                  'linear-gradient(to bottom right, rgba(248,113,113,0.20), rgba(250,204,21,0.20))',
                borderColor: 'var(--nad-b2)',
              }}
            >
              <Lock size={32} style={{ color: 'var(--nad-neg)' }} />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--nad-t1)' }}>
              PCC Access Restricted
            </h2>
            <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--nad-t3)' }}>
              You do not have permission to access the Process Command Center. Please contact your
              administrator to request access.
            </p>
          </Card3D>
        </div>
        </div>
      </>
    );
  }

  const tabs = [
    {
      id: 'details',
      label: 'Project Details',
      icon: FileText,
      badge: pendingApprovalsCount > 0 ? pendingApprovalsCount : null,
      badgeColor: 'blue',
      enabled: true,
    },
    {
      id: 'computation',
      label: 'Project Computation',
      icon: Calculator,
      badge: isOamUser && exportReadyCount > 0 ? exportReadyCount : null,
      badgeColor: 'green',
      enabled: canAccessComputation,
    },
  ];

  const moduleSubmenuAccess = (user?.module_access?.pcc?.submenu_access) || {};
  const visibleTabs = useMemo(() => {
    return tabs.filter((t) => {
      if (moduleSubmenuAccess[t.id]?.visible === false) return false;
      if (!t.enabled) return false;
      return true;
    });
  }, [tabs, moduleSubmenuAccess]);
  const setActiveTab = useCallback((id) => {
    if (visibleTabs.find((t) => t.id === id)) {
      setActiveTabRaw(id);
    } else if (visibleTabs[0]) {
      setActiveTabRaw(visibleTabs[0].id);
    }
  }, [visibleTabs]);
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    const activeExists = visibleTabs.some((t) => t.id === activeTab);
    if (!activeExists) {
      setActiveTabRaw(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  return (
    <>
      <PCCIntroCurtain show={!minElapsed} isLight={isLight} />
      <div className="space-y-6">
      <div {...stagger(0)}>
        <Card3D className="content-glass rounded-2xl p-2" intensity={3} lift={6} tilt={false}>
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-xl border flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(to bottom right, rgba(236,72,153,0.20), rgba(168,85,247,0.15))',
                borderColor: 'var(--nad-b2)',
              }}
            >
              <Cpu size={20} style={{ color: 'var(--nad-accent-txt)' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ color: 'var(--nad-t1)' }}>
                Process Command Center
              </h2>
              <p className="text-xs" style={{ color: 'var(--nad-t3)' }}>
                Project oversight, approvals workflow, and computation management
              </p>
            </div>
          </div>
          </div>

          <div className="px-2 pb-2">
            <div
              className="flex items-center gap-1 p-1 rounded-xl"
              style={{ background: 'var(--nad-s1)' }}
            >
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const disabled = !tab.enabled;
                return (
                  <button
                    key={tab.id}
                    disabled={disabled}
                    onClick={() => !disabled && setActiveTab(tab.id)}
                    className={`relative flex-1 min-w-0 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    style={{
                      background: isActive
                        ? 'linear-gradient(to right, rgba(250,204,21,0.18), rgba(59,130,246,0.12))'
                        : 'transparent',
                      color: isActive ? 'var(--nad-accent-txt)' : disabled ? 'var(--nad-t4)' : 'var(--nad-t3)',
                      boxShadow: isActive
                        ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 2px rgba(0,0,0,0.08)'
                        : undefined,
                    }}
                  >
                    <Icon size={16} className="flex-shrink-0" />
                    <span className="truncate">{tab.label}</span>
                    {tab.badge != null && (
                      <span
                        className="flex-shrink-0 inline-flex items-center justify-center text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{
                          background:
                            tab.badgeColor === 'green'
                              ? 'rgba(34,197,94,0.20)'
                              : 'rgba(59,130,246,0.20)',
                          color:
                            tab.badgeColor === 'green'
                              ? 'rgb(34,197,94)'
                              : 'rgb(59,130,246)',
                        }}
                      >
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </Card3D>
      </div>

      <div {...stagger(1)}>
        {activeTab === 'details' && (
          <ProjectDetails axiosInstance={axiosInstance} currentUser={user} />
        )}
        {activeTab === 'computation' && canAccessComputation && (
          <ProjectComputation axiosInstance={axiosInstance} currentUser={user} />
        )}
      </div>
      </div>
    </>
  );
}
